import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  UserPlus,
  Mail,
  MailCheck,
  MailWarning,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Trash2,
  Loader2,
  Users,
  Clock,
  Phone,
  Ban,
  MoreHorizontal,
  PowerOff,
  Power,
} from "lucide-react";

interface ClientUsersTabProps {
  companyId: number;
  channelId?: number | null;
  readOnly?: boolean;
}

export default function ClientUsersTab({ companyId, channelId, readOnly }: ClientUsersTabProps) {
  const utils = trpc.useUtils();

  // State para dialog de orientação do novo cadastro completo
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);

  // State para confirmação de ação
  const [actionTarget, setActionTarget] = useState<{
    id: number;
    name: string | null;
    action: "deactivate" | "delete";
  } | null>(null);

  // Queries
  const { data: clientUsers, isLoading } = trpc.clients.listUsers.useQuery(
    { companyId },
    { enabled: !!companyId }
  );

  // Mutations

  const removeUserMutation = trpc.clients.removeUser.useMutation({
    onSuccess: () => {
      toast.success("Usuário excluído com sucesso");
      utils.clients.listUsers.invalidate({ companyId });
      setActionTarget(null);
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao excluir usuário");
    },
  });

  const deactivateUserMutation = trpc.clients.deactivateUser.useMutation({
    onSuccess: (result) => {
      toast.success(result.message);
      utils.clients.listUsers.invalidate({ companyId });
      setActionTarget(null);
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao inativar usuário");
    },
  });

  const reactivateUserMutation = trpc.clients.reactivateUser.useMutation({
    onSuccess: (result) => {
      toast.success(result.message);
      utils.clients.listUsers.invalidate({ companyId });
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao reativar usuário");
    },
  });

  const resendInviteMutation = trpc.clients.resendInvite.useMutation({
    onSuccess: (result) => {
      toast.success(result.message);
      utils.clients.listUsers.invalidate({ companyId });
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao reenviar convite");
    },
  });

  const handleOpenCompleteRegistration = () => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams();
      params.set("companyId", String(companyId));
      if (channelId && channelId > 0) {
        params.set("channelId", String(channelId));
      }
      window.location.href = `/configuracoes/usuarios?${params.toString()}`;
    }
  };

  const getStatusBadge = (user: NonNullable<typeof clientUsers>[number]) => {
    // Excluído tem prioridade sobre inativo
    if ((user as any).isDeleted) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
          <Ban className="w-3 h-3" /> Excluído
        </span>
      );
    }
    if (!user.isActive) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
          <XCircle className="w-3 h-3" /> Inativo
        </span>
      );
    }
    if (user.hasPassword) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
          <CheckCircle2 className="w-3 h-3" /> Ativo
        </span>
      );
    }
    if (user.inviteExpired) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
          <MailWarning className="w-3 h-3" /> Convite expirado
        </span>
      );
    }
    if (user.invitePending) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
          <Clock className="w-3 h-3" /> Convite pendente
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
        <Mail className="w-3 h-3" /> Sem convite
      </span>
    );
  };

  // Separar: ativos, inativos (não excluídos), excluídos
  const activeUsers = clientUsers?.filter(u => u.isActive && !(u as any).isDeleted) || [];
  const deletedUsers = clientUsers?.filter(u => (u as any).isDeleted) || [];
  const inactiveUsers = clientUsers?.filter(u => !u.isActive && !(u as any).isDeleted) || [];

  const isAnyMutationPending = removeUserMutation.isPending || deactivateUserMutation.isPending || reactivateUserMutation.isPending;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Usuários do Cliente (RH)</h3>
          <p className="text-sm text-gray-500 mt-1">
            Gerencie os usuários RH que terão acesso à plataforma para esta empresa.
            {activeUsers.length > 0 && (
              <span className="ml-2 text-gray-700 font-medium">
                {activeUsers.length} ativo{activeUsers.length !== 1 ? "s" : ""}
              </span>
            )}
          </p>
        </div>

        {!readOnly && (
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2">
                <UserPlus className="w-4 h-4" />
                Adicionar Usuário RH
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Cadastrar Usuário RH</DialogTitle>
                <DialogDescription>
                  O cadastro rápido desta aba foi descontinuado para garantir a nova jornada completa com papel estrutural, serviços liberados e perfil funcional definido antes do convite.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-4 text-sm text-muted-foreground">
                <p>
                  Para concluir o cadastro corretamente, use a tela administrativa de usuários. Lá será possível definir todos os vínculos obrigatórios em uma única jornada antes de enviar o convite.
                </p>
                <p>
                  A empresa atual será preservada como contexto inicial e, quando disponível, o canal também seguirá pré-informado para agilizar o preenchimento.
                </p>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setIsAddDialogOpen(false)}
                >
                  Fechar
                </Button>
                <Button
                  onClick={handleOpenCompleteRegistration}
                  className="gap-2"
                >
                  <UserPlus className="w-4 h-4" />
                  Abrir cadastro completo
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          <span className="ml-2 text-gray-500">Carregando usuários...</span>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && (!clientUsers || clientUsers.length === 0) && (
        <div className="flex flex-col items-center justify-center py-12 border-2 border-dashed border-gray-200 rounded-lg">
          <Users className="w-12 h-12 text-gray-300 mb-3" />
          <p className="text-gray-500 font-medium">Nenhum usuário RH cadastrado</p>
          <p className="text-sm text-gray-400 mt-1">
            Adicione usuários RH para que eles possam acessar a plataforma.
          </p>
        </div>
      )}

      {/* Users table */}
      {!isLoading && clientUsers && clientUsers.length > 0 && (
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="font-semibold">Nome</TableHead>
                <TableHead className="font-semibold">E-mail</TableHead>
                <TableHead className="font-semibold">Telefone</TableHead>
                <TableHead className="font-semibold">Status</TableHead>
                <TableHead className="font-semibold">Criado em</TableHead>
                {!readOnly && <TableHead className="font-semibold text-right">Ações</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {/* Usuários ativos */}
              {activeUsers.map((user) => (
                <TableRow key={user.id}>
                  <TableCell className="font-medium">{user.name || "—"}</TableCell>
                  <TableCell className="text-gray-600">{user.email}</TableCell>
                  <TableCell className="text-gray-600">
                    {user.phone ? (
                      <span className="inline-flex items-center gap-1">
                        <Phone className="w-3 h-3" />
                        {user.phone}
                      </span>
                    ) : "—"}
                  </TableCell>
                  <TableCell>{getStatusBadge(user)}</TableCell>
                  <TableCell className="text-gray-500 text-sm">
                    {user.createdAt ? new Date(user.createdAt).toLocaleDateString("pt-BR") : "—"}
                  </TableCell>
                  {!readOnly && (
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {/* Reenviar convite (apenas se não tem senha) */}
                          {!user.hasPassword && (
                            <DropdownMenuItem
                              onClick={() => resendInviteMutation.mutate({ companyId, userId: user.id })}
                              disabled={resendInviteMutation.isPending}
                            >
                              <RefreshCw className="w-4 h-4 mr-2 text-blue-600" />
                              Reenviar convite
                            </DropdownMenuItem>
                          )}
                          {/* Inativar */}
                          <DropdownMenuItem
                            onClick={() => setActionTarget({ id: user.id, name: user.name, action: "deactivate" })}
                          >
                            <PowerOff className="w-4 h-4 mr-2 text-orange-500" />
                            Inativar
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {/* Excluir */}
                          <DropdownMenuItem
                            onClick={() => setActionTarget({ id: user.id, name: user.name, action: "delete" })}
                            className="text-red-600 focus:text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Excluir
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  )}
                </TableRow>
              ))}

              {/* Inativos (não excluídos) — com botão de Reativar */}
              {inactiveUsers.length > 0 && (
                <>
                  <TableRow>
                    <TableCell colSpan={readOnly ? 5 : 6} className="bg-gray-50 text-sm text-gray-500 font-medium py-2">
                      Inativos ({inactiveUsers.length})
                    </TableCell>
                  </TableRow>
                  {inactiveUsers.map((user) => (
                    <TableRow key={user.id} className="opacity-70">
                      <TableCell className="font-medium">{user.name || "—"}</TableCell>
                      <TableCell className="text-gray-600">{user.email}</TableCell>
                      <TableCell className="text-gray-600">
                        {user.phone ? (
                          <span className="inline-flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {user.phone}
                          </span>
                        ) : "—"}
                      </TableCell>
                      <TableCell>{getStatusBadge(user)}</TableCell>
                      <TableCell className="text-gray-500 text-sm">
                        {user.createdAt ? new Date(user.createdAt).toLocaleDateString("pt-BR") : "—"}
                      </TableCell>
                      {!readOnly && (
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => reactivateUserMutation.mutate({ companyId, userId: user.id })}
                                disabled={reactivateUserMutation.isPending}
                              >
                                <Power className="w-4 h-4 mr-2 text-green-600" />
                                Reativar
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => setActionTarget({ id: user.id, name: user.name, action: "delete" })}
                                className="text-red-600 focus:text-red-600"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Excluir
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </>
              )}

              {/* Excluídos — sem ações */}
              {deletedUsers.length > 0 && (
                <>
                  <TableRow>
                    <TableCell colSpan={readOnly ? 5 : 6} className="bg-gray-50 text-sm text-gray-500 font-medium py-2">
                      Excluídos ({deletedUsers.length})
                    </TableCell>
                  </TableRow>
                  {deletedUsers.map((user) => (
                    <TableRow key={user.id} className="opacity-50">
                      <TableCell className="font-medium">{user.name || "—"}</TableCell>
                      <TableCell className="text-gray-600">{user.email}</TableCell>
                      <TableCell className="text-gray-600">
                        {user.phone ? (
                          <span className="inline-flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {user.phone}
                          </span>
                        ) : "—"}
                      </TableCell>
                      <TableCell>{getStatusBadge(user)}</TableCell>
                      <TableCell className="text-gray-500 text-sm">
                        {user.createdAt ? new Date(user.createdAt).toLocaleDateString("pt-BR") : "—"}
                      </TableCell>
                      {!readOnly && <TableCell />}
                    </TableRow>
                  ))}
                </>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Confirmação de ação (Inativar ou Excluir) */}
      <AlertDialog open={!!actionTarget} onOpenChange={(open) => !open && setActionTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {actionTarget?.action === "deactivate" ? "Inativar usuário RH" : "Excluir usuário RH"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {actionTarget?.action === "deactivate" ? (
                <>
                  Tem certeza que deseja inativar <strong>{actionTarget?.name || "este usuário"}</strong>?
                  O usuário perderá acesso à plataforma, mas poderá ser reativado posteriormente.
                </>
              ) : (
                <>
                  Tem certeza que deseja excluir <strong>{actionTarget?.name || "este usuário"}</strong>?
                  O usuário será excluído permanentemente e não poderá ser reativado.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isAnyMutationPending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (!actionTarget) return;
                if (actionTarget.action === "deactivate") {
                  deactivateUserMutation.mutate({ companyId, userId: actionTarget.id });
                } else {
                  removeUserMutation.mutate({ companyId, userId: actionTarget.id });
                }
              }}
              disabled={isAnyMutationPending}
              className={actionTarget?.action === "deactivate" ? "bg-orange-600 hover:bg-orange-700" : "bg-red-600 hover:bg-red-700"}
            >
              {isAnyMutationPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  {actionTarget?.action === "deactivate" ? "Inativando..." : "Excluindo..."}
                </>
              ) : (
                actionTarget?.action === "deactivate" ? "Inativar" : "Excluir"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
